import React, { useState, useEffect } from 'react';
import { SpeedDial, SpeedDialData } from '@client/components/SpeedDial';
import SpeedDialEdit from '@client/components/SpeedDial/SpeedDialEdit';

type Theme = 'light' | 'dark' | 'system';

export const CoursesPage: React.FC = () => {
    // State for global column fallback
    const [defaultColumns, setDefaultColumns] = useState<number>(5);
    const [coursesData, setCoursesData] = useState<Record<string, any> | null>(null);
    const [activeTab, setActiveTab] = useState<string>('');
    const [isEditMode, setIsEditMode] = useState(false);
    // Theme State
    const [theme, setTheme] = useState<Theme>(() => {
        if (typeof localStorage !== 'undefined') {
            const saved = localStorage.getItem('theme');
            return (saved as Theme) || 'system';
        }
        return 'system';
    });
    const handleGlobalSave = async (speedDialData: SpeedDialData) => {
        setCoursesData(speedDialData);
        setIsEditMode(false);
        const rawResponse = await fetch('/api/courses', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(speedDialData),
        });
        const content = await rawResponse.json();

        console.log(content);
    };
    const onTabChange = (tab: string) => {
        console.log('tabChange', tab);
        setActiveTab(tab);
    };
    // Theme Logic
    useEffect(() => {
        document.title = 'Cursos';
        const root = document.documentElement;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const applyTheme = () => {
            const isDark = theme === 'dark' || (theme === 'system' && mediaQuery.matches);

            if (isDark) {
                root.classList.add('dark');
            } else {
                root.classList.remove('dark');
            }
        };

        applyTheme();
        localStorage.setItem('theme', theme);

        const handleSystemChange = () => {
            if (theme === 'system') applyTheme();
        };

        mediaQuery.addEventListener('change', handleSystemChange);
        return () => mediaQuery.removeEventListener('change', handleSystemChange);
    }, [theme]);

    useEffect(() => {
        console.log('empty use effect');
        const fetchCourseStructure = async () => {
            try {
                const response = await fetch(`/api/courses`);
                if (response.ok) {
                    const data = await response.json();
                    setCoursesData(data);
                }
            } catch (error) {
                console.error('Error fetching course structure:', error);
            }
        };
        fetchCourseStructure();
    }, []);
    const cycleTheme = () => {
        const modes: Theme[] = ['light', 'dark', 'system'];
        const nextIndex = (modes.indexOf(theme) + 1) % modes.length;
        setTheme(modes[nextIndex]);
    };

    return !coursesData ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-slate-500">
            <svg
                className="w-16 h-16 mb-4 text-gray-300 dark:text-slate-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
            </svg>
            <p className="text-lg">Loading</p>
        </div>
    ) : (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 text-slate-800 dark:text-slate-100 flex flex-col transition-colors duration-300">
            {/* Header / Toolbar */}
            <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-50 border-b border-gray-200 dark:border-slate-800 shadow-sm transition-colors duration-300">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-lg transition-colors ${isEditMode ? 'bg-orange-500 shadow-orange-500/30' : 'bg-blue-600 shadow-blue-500/30'}`}
                        >
                            {isEditMode ? (
                                <svg
                                    className="w-5 h-5 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                    />
                                </svg>
                            ) : (
                                <svg
                                    className="w-5 h-5 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 10V3L4 14h7v7l9-11h-7z"
                                    />
                                </svg>
                            )}
                        </div>
                        <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                            {isEditMode ? 'Speed Dial Editor' : 'Speed Dial'}
                        </h1>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="h-6 w-px bg-gray-200 dark:bg-slate-700 hidden sm:block"></div>

                        {/* Columns Control (Only visible in Read Mode for global default) */}
                        {!isEditMode && (
                            <div className="hidden sm:flex items-center gap-4 bg-gray-50 dark:bg-slate-800 px-4 py-2 rounded-full border border-gray-200 dark:border-slate-700 transition-colors">
                                <label
                                    htmlFor="columns"
                                    className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider"
                                >
                                    Cols: {defaultColumns}
                                </label>
                                <input
                                    type="range"
                                    id="columns"
                                    min="2"
                                    max="8"
                                    step="1"
                                    value={defaultColumns}
                                    onChange={(e) => setDefaultColumns(Number(e.target.value))}
                                    className="w-24 h-2 bg-gray-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer accent-blue-600 hover:accent-blue-700 focus:outline-none"
                                />
                            </div>
                        )}

                        {/* Theme Toggle */}
                        <button
                            onClick={cycleTheme}
                            className="p-2.5 rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                            title={`Current theme: ${theme}`}
                            aria-label="Toggle theme"
                        >
                            {theme === 'light' && (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                                    />
                                </svg>
                            )}
                            {theme === 'dark' && (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                                    />
                                </svg>
                            )}
                            {theme === 'system' && (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-grow w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {isEditMode ? (
                    <SpeedDialEdit
                        data={coursesData}
                        defaultColumns={defaultColumns}
                        onTabChange={onTabChange}
                        onSave={handleGlobalSave}
                        defaultActiveTab={activeTab}
                        onEnableEdit={setIsEditMode}
                    />
                ) : (
                    <SpeedDial
                        data={coursesData}
                        defaultColumns={defaultColumns}
                        onTabChange={onTabChange}
                        defaultActiveTab={activeTab}
                        onEnableEdit={setIsEditMode}
                    />
                )}
            </main>
        </div>
    );
};
